import * as v2 from '@/core/services/api/v2';

/**
 * Upgrades de atributo, no V2.
 *
 * Uma requisição para tudo: `GET /player/:id/upgrades` devolve níveis **e**
 * custos — a versão Strapi fazia nove (uma de níveis, oito de custo). E o
 * cliente não calcula custo nenhum: o `calculateUpgradeCost` local, que já
 * divergiu do servidor uma vez (`luck` 7,5 contra 4,5), saiu daqui de vez.
 */

const state = {
  upgradeStats: null,
  costs: {},
  loading: false,
  error: null
};

const mutations = {
  SET_UPGRADE_STATS(state, stats) {
    state.upgradeStats = stats;
  },
  SET_COSTS(state, costs) {
    state.costs = costs;
  },
  SET_STAT_LEVEL(state, { stat, level, cost }) {
    state.upgradeStats = { ...(state.upgradeStats || {}), [stat]: level };
    state.costs = { ...state.costs, [stat]: cost };
  },
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  }
};

const actions = {
  async fetchUpgradeStats({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return;

    commit('SET_LOADING', true);
    try {
      const { stats, costs } = await v2.upgrades(playerId);
      commit('SET_UPGRADE_STATS', stats);
      commit('SET_COSTS', costs);
      commit('SET_ERROR', null);
    } catch (error) {
      const mensagem =
        error.response?.data?.message || error.message || 'Failed to fetch upgrade stats';
      console.error('Error fetching upgrade stats:', mensagem);
      commit('SET_ERROR', mensagem);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Aplica um upgrade. A resposta já diz o novo nível, o ouro restante e o
   * custo do próximo degrau — o estado local é atualizado com o que o servidor
   * afirmou, sem refetch.
   */
  async upgradeStat({ commit, rootState }, statName) {
    const playerId = rootState.player?.id;
    if (!playerId) return { success: false, error: 'Player ID not found' };

    commit('SET_LOADING', true);
    try {
      const resultado = await v2.aplicarUpgrade(playerId, statName);

      commit('SET_STAT_LEVEL', {
        stat: resultado.stat,
        level: resultado.level,
        cost: resultado.nextCost
      });
      commit('player/SET_GOLD', resultado.gold, { root: true });

      return { success: true, data: resultado };
    } catch (error) {
      // O V2 responde `{ message }`; o Strapi respondia `{ error: { message } }`.
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        'Failed to upgrade stat';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

const getters = {
  getUpgradeStats: state => state.upgradeStats,
  getStatLevel: state => (statName) => {
    return state.upgradeStats ? state.upgradeStats[statName] || 0 : 0;
  },
  getStatCost: state => (statName) => {
    return state.costs[statName] || 0;
  },
  isLoading: state => state.loading,
  getError: state => state.error
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
